import { useEffect, useState } from "react";
import { createPortal } from "react-dom";
import { useSearchParams } from "react-router-dom";
import { User } from "firebase/auth";
// Local
import { useActiveCallStore } from "../../store/active-call";
import { useIncomingCallsStore } from "../../store/incoming-calls";
import { DbRead } from "../../database";
import { ExistingCustomer } from "../../models/customer";
import { logger } from "../../logging";
import BubbleActiveCall from "./BubbleActiveCall";
import BubbleUnmatchedCall from "./BubbleUnmatchedCall";
import { useAuthStore } from "../../store/firebase-auth";
import { createToastMessageID } from "../../utils";
import { useToastMessageStore } from "../../store/toast-messages";
import * as strings from "../../strings";

interface Props {
  siteKey: string;
}

export default function GlobalCallManager({ siteKey }: Props): JSX.Element {
  const {
    activeCall,
    setActiveCall,
    addPhoneToCustomer,
    clearActiveCall,
    fetch: fetchActiveCall,
  } = useActiveCallStore();
  const {
    currentCall: incomingCall,
    clearCall: clearIncomingCall,
    customerContextHistory,
    updateCallCustomer,
  } = useIncomingCallsStore();
  const firebaseUser = useAuthStore((state) => state.firebaseUser) as User;

  const [searchParams] = useSearchParams();
  const paramsCallID = searchParams.get("activeCall");

  // active call
  const [matchedCustomer, setMatchedCustomer] =
    useState<ExistingCustomer | null>(null);
  // incoming call
  const [selectedCustomer, setSelectedCustomer] =
    useState<ExistingCustomer | null>(null);

  const addMessage = useToastMessageStore((state) => state.addToastMessage);

  useEffect(() => {
    async function getRecentCustomer() {
      if (
        customerContextHistory.length &&
        customerContextHistory[0].customerID
      ) {
        const c = await DbRead.customers.get(
          siteKey,
          customerContextHistory[0].customerID,
        );
        setSelectedCustomer(c);
      }
    }
    getRecentCustomer();
  }, [customerContextHistory, siteKey]);

  useEffect(() => {
    if (!paramsCallID) return;
    fetchActiveCall(siteKey, paramsCallID);
  }, [fetchActiveCall, paramsCallID, siteKey]);

  useEffect(() => {
    function subscribeToCustomer() {
      if (!activeCall || typeof activeCall.customerID !== "string") {
        setMatchedCustomer(null);
        return undefined;
      }
      const listener = DbRead.customers.subscribe({
        siteKey,
        customerID: activeCall.customerID,
        onChange: setMatchedCustomer,
        onError: (error) => {
          logger.error("Error fetching customer in GlobalCallManager:", error);
          setMatchedCustomer(null);
        },
      });
      return listener;
    }
    const unsubscribe = subscribeToCustomer();
    return () => unsubscribe && unsubscribe();
  }, [activeCall, siteKey]);

  const callDocID = incomingCall?.id ?? activeCall?.id;
  useEffect(() => {
    function clearTheCall() {
      if (!callDocID) return undefined;
      const unsubscribeFn = DbRead.calls.subscribe({
        siteKey: siteKey,
        docID: callDocID,
        onChange: (call) => {
          if (
            call?.callStatus === "canceled" ||
            call?.callStatus === "completed" ||
            call?.callStatus === "failed" ||
            call?.callStatus === "no-answer" ||
            call?.callStatus === "cleared"
          ) {
            clearActiveCall();
            clearIncomingCall();
          }
        },
        onError: logger.error,
      });
      return unsubscribeFn;
    }
    const unsubscribe = clearTheCall();
    return () => unsubscribe && unsubscribe();
  }, [callDocID, clearActiveCall, clearIncomingCall, siteKey]);

  async function updateCall() {
    if (!selectedCustomer) throw new Error("No customer found");

    await updateCallCustomer(selectedCustomer.id);

    if (incomingCall) {
      // if customerID doesn't get added here, the user will have to click the
      // call in TopNav again, in order for BubbleActiveCall to show up. (subscribeToCustomer will return early if there's no customerID)
      setActiveCall({ ...incomingCall, customerID: selectedCustomer.id });
    }
    clearIncomingCall();

    const existingPhone = selectedCustomer?.phone;
    const nonEmpty =
      existingPhone && existingPhone.length > 0 && existingPhone[0].length > 0;
    await addPhoneToCustomer({
      customerRefPath: selectedCustomer!.refPath, // i wouldn't normally advocate for bang usage, but we wouldn't have gotten to the onSuccess callback if customer was null
      phoneAlreadyExists: nonEmpty === true,
      uid: firebaseUser.uid,
    });
    addMessage({
      id: createToastMessageID(),
      message: strings.successfulUpdate(selectedCustomer!.name),
      dialog: false,
      type: "success",
    });
  }

  const renderCallBubble = () => {
    if (matchedCustomer) {
      return (
        <BubbleActiveCall customer={matchedCustomer} activeCall={activeCall} />
      );
    }
    if (incomingCall) {
      return (
        <BubbleUnmatchedCall
          siteKey={siteKey}
          customer={selectedCustomer}
          incomingCall={incomingCall}
          clearIncomingCall={clearIncomingCall}
          updateCall={updateCall}
        />
      );
    }
    return null;
  };

  const portalContent = (
    <div className="fixed left-5 top-20 z-[100] h-0 w-0">
      {renderCallBubble()}
    </div>
  );
  return createPortal(portalContent, document.body);
}
